// 3.4 / 3.5 and the run screens: workflow versions, service endpoints and the 30-day run log. Two ways, as hubStore
// and codeStore (LH-B3):
//   fixtures (default, every test): persisted in this browser (LH-F3); this file writes History and Alerts and keeps
//     the lender's workflow summary (Lenders list, header, the single switch) in step
//   the API (VITE_HUB_API=1): a cache of the Lender Hub API. An action still changes the screen at once (same rules,
//     src/lib/flow.ts), then its call goes to the API in order (one queue with the lender and code calls); the API's
//     answer replaces the local copy. The API runs the path checks, C1, the switches, the runner's containers, the
//     test calls, the run log, the codes and the alerts itself, and derives the lender's workflow summary.
// Rules live in src/lib/flow.ts, the walk in src/lib/flowEngine.ts (fixtures) or on the API. The run log holds
// reference ids only: the Hub is not a lead store.
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { ENDPOINTS, FLOWS, RUNS } from '@/fixtures/flows'
import { applyFlowSave, applyPathsRun, applySwitch, copyFlow, nextFlowVersion, starterFlow, type FlowIssue, type FlowPath } from '@/lib/flow'
import { api, apiMode } from '@/lib/hubApi'
import { useCodeStore } from '@/store/codeStore'
import { extraLoads, refreshLender, remote, useHubStore } from '@/store/hubStore'
import { useSettingsStore } from '@/store/settingsStore'
import type { Lender, ServiceEndpoint, WorkflowRun, WorkflowVersion } from '@/types'
import { batchesOfProduct } from '@/lib/batch'

const nowIso = () => new Date().toISOString()
const short = (name: string) => name.split('.').slice(1).join('.')
const enc = encodeURIComponent

export interface NewWorkflowInput {
  lenderSlug: string
  productSlug: string
  name: string
  version: string
  batch: WorkflowVersion['batch']
  /** blank = Start + the chosen API files in a row; copy = a copy of an existing version */
  start: { kind: 'blank'; files: string[] } | { kind: 'copy'; fromId: string }
}

export interface FlowState {
  flows: WorkflowVersion[]
  endpoints: ServiceEndpoint[]
  runs: WorkflowRun[]

  saveFlow: (id: string, patch: Pick<WorkflowVersion, 'nodes' | 'edges' | 'settings' | 'batch'>, note: string, who: string) => WorkflowVersion
  recordPaths: (id: string, paths: FlowPath[], issues: FlowIssue[], who: string) => WorkflowVersion
  newWorkflow: (input: NewWorkflowInput, who: string) => WorkflowVersion
  newVersionFrom: (fromId: string, who: string) => WorkflowVersion
  /** Make live from 3.4: the version goes behind the product's production endpoint (the same as Switch version in 3.5). */
  makeLive: (flowId: string, who: string) => ServiceEndpoint
  switchEndpoint: (endpointId: string, toVersion: string, who: string) => ServiceEndpoint
  setEndpointState: (endpointId: string, state: ServiceEndpoint['state'], reason: string | null, who: string) => void
  createEndpoint: (ep: Pick<ServiceEndpoint, 'id' | 'lenderSlug' | 'productSlug' | 'workflowName' | 'workflowVersion' | 'env'>, who: string) => ServiceEndpoint
  /** a finished run → the run log, the endpoint's counters, the error-rate rule (fixtures; the API records its own runs) */
  recordRun: (run: WorkflowRun) => void
  reset: () => void
}

// ---- the API side ----------------------------------------------------------------------------------------------

/** fixtures only: History / Alerts / the lender's summary made here; on the API the API makes them */
const local = (fn: () => void) => { if (!apiMode()) fn() }

const putFlow = (f: WorkflowVersion) => useFlowStore.setState((s) => ({ flows: s.flows.some((x) => x.id === f.id) ? s.flows.map((x) => (x.id === f.id ? f : x)) : [f, ...s.flows] }))
const putFlows = (slug: string, list: WorkflowVersion[]) => useFlowStore.setState((s) => ({ flows: [...list, ...s.flows.filter((f) => f.lenderSlug !== slug)] }))
const putEndpoint = (e: ServiceEndpoint) => useFlowStore.setState((s) => ({ endpoints: s.endpoints.some((x) => x.id === e.id) ? s.endpoints.map((x) => (x.id === e.id ? e : x)) : [...s.endpoints, e] }))
const putEndpoints = (slug: string, list: ServiceEndpoint[]) => useFlowStore.setState((s) => ({ endpoints: [...s.endpoints.filter((e) => e.lenderSlug !== slug), ...list] }))

/** API mode: the call after the earlier ones; its answer applied; the lender (its workflow summary) read again. */
function send<T>(slug: string, call: () => Promise<T>, apply: (res: T) => void) {
  remote(call, (res) => {
    apply(res)
    void refreshLender(slug).catch(() => {})
  })
}

/** One lender's workflows and endpoints read again (after a switch: the statuses of several versions moved). */
export async function refreshFlows(slug: string) {
  const [flows, endpoints] = await Promise.all([api<WorkflowVersion[]>('GET', `/lenders/${enc(slug)}/workflows`), api<ServiceEndpoint[]>('GET', `/lenders/${enc(slug)}/endpoints`)])
  putFlows(slug, flows)
  putEndpoints(slug, endpoints)
}

/** Every endpoint read again with its counters (Active endpoints). */
export async function refreshEndpoints() {
  useFlowStore.setState({ endpoints: await api<ServiceEndpoint[]>('GET', '/endpoints') })
}

/** The run log read again (after a test call; the Workflow runs screen). */
export async function refreshRuns() {
  const runs = await api<WorkflowRun[]>('GET', '/runs?days=30')
  useFlowStore.setState({ runs })
}

/** 3.4 / 3.5 and the run log from the API for these lenders (at sign-in, and after a refusal). */
export async function loadFlowsFromHub(slugs: string[]) {
  const [flows, endpoints, runs] = await Promise.all([
    Promise.all(slugs.map((slug) => api<WorkflowVersion[]>('GET', `/lenders/${enc(slug)}/workflows`))).then((x) => x.flat()),
    api<ServiceEndpoint[]>('GET', '/endpoints'),
    api<WorkflowRun[]>('GET', '/runs?days=30'),
  ])
  useFlowStore.setState({ flows, endpoints, runs })
}
extraLoads.push(loadFlowsFromHub)

// ---- fixtures only ------------------------------------------------------------------------------------------------

/** The lender's workflow summary (F1's Lenders list, header and switch dialog) from the endpoints and drafts. */
function syncLender(slug: string, flows: WorkflowVersion[], endpoints: ServiceEndpoint[]) {
  if (apiMode()) return
  const hub = useHubStore.getState()
  const lender = hub.lender(slug)
  if (!lender) return
  const behind = endpoints.filter((e) => e.lenderSlug === slug && e.state !== 'retired')
  const summary: Lender['workflows'] = behind.map((e) => {
    const f = flows.find((x) => x.name === e.workflowName && x.version === e.workflowVersion)
    const state = lender.status === 'off' || e.state === 'suspended' ? 'suspended' : f && (f.status === 'draft' || f.status === 'paths_passed') ? 'draft' : 'live'
    return { name: e.workflowName, version: e.workflowVersion, endpointId: e.id, state }
  })
  for (const f of flows.filter((x) => x.lenderSlug === slug && (x.status === 'draft' || x.status === 'paths_passed')))
    if (!summary.some((w) => w.name === f.name && w.version === f.version)) summary.push({ name: f.name, version: f.version, endpointId: '—', state: 'draft' })
  hub.patchLender(slug, { workflows: summary })
}

export const useFlowStore = create<FlowState>()(
  persist(
    (set, get) => {
      const put = (f: WorkflowVersion) => set({ flows: get().flows.map((x) => (x.id === f.id ? f : x)) })
      const hist = (who: string, slug: string, where: string, what: string) => local(() => useHubStore.getState().addHistory(who, slug, where, what))
      return {
        flows: apiMode() ? [] : FLOWS,
        endpoints: apiMode() ? [] : ENDPOINTS,
        runs: apiMode() ? [] : RUNS,

        saveFlow: (id, patch, note, who) => {
          const f = get().flows.find((x) => x.id === id)
          if (!f) throw new Error('workflow not found')
          const saved = applyFlowSave(f, patch, note, who, nowIso())
          put(saved)
          hist(who, f.lenderSlug, `${short(f.name)} ${f.version}`, `Draft saved: ${saved.nodes.length} nodes, ${saved.edges.length} lines${note.trim() ? ` · “${note.trim()}”` : ''}`)
          syncLender(f.lenderSlug, get().flows, get().endpoints)
          send(f.lenderSlug, () => api<WorkflowVersion>('PUT', `/workflows/${enc(id)}`, { ...patch, note }), putFlow)
          return saved
        },

        recordPaths: (id, paths, issues, who) => {
          const f = get().flows.find((x) => x.id === id)
          if (!f) throw new Error('workflow not found')
          const next = applyPathsRun(f, paths, issues, who, nowIso())
          put(next)
          hist(who, f.lenderSlug, `${short(f.name)} ${f.version}`, `Run all paths: ${next.pathsRun!.ok} / ${next.pathsRun!.total} end at an Answer${issues.length ? `, ${issues.length} check(s) open` : ''}${next.status === 'paths_passed' ? ' → paths passed' : ''}`)
          syncLender(f.lenderSlug, get().flows, get().endpoints)
          // the API enumerates the paths and runs the checks itself; its answer decides "paths passed"
          send(f.lenderSlug, () => api<{ workflow: WorkflowVersion }>('POST', `/workflows/${enc(id)}/paths`, {}), (r) => putFlow(r.workflow))
          return next
        },

        newWorkflow: (input, who) => {
          const at = nowIso()
          if (get().flows.some((f) => f.name === input.name && f.version === input.version)) throw new Error(`${input.name} ${input.version} already exists`)
          let flow: WorkflowVersion
          if (input.start.kind === 'copy') {
            const fromId = input.start.fromId
            const from = get().flows.find((f) => f.id === fromId)
            if (!from) throw new Error('the version to copy does not exist')
            flow = copyFlow(from, input.version, who, at, { name: input.name, productSlug: input.productSlug, batch: input.batch, madeFrom: apiMode() ? from.version : `${short(from.name)} ${from.version}`, note: `Copied from ${short(from.name)} ${from.version}` })
          } else {
            const batches = batchesOfProduct(useCodeStore.getState().batches, input.lenderSlug, input.productSlug)
            const batch = input.batch.mode === 'live' ? batches.find((b) => b.status === 'live') : batches.find((b) => input.batch.mode === 'pinned' && b.version === input.batch.version)
            flow = {
              id: `wf_${input.lenderSlug}_${Date.now().toString(36)}`,
              lenderSlug: input.lenderSlug, name: input.name, productSlug: input.productSlug, version: input.version, status: 'draft', batch: input.batch,
              ...starterFlow(input.start.files, batch), settings: { waitPerCallS: 8, totalWaitS: 45 },
              pathsRun: null, savedBy: who, savedAt: at, note: 'New workflow', madeFrom: null, madeLiveAt: null,
            }
          }
          set({ flows: [flow, ...get().flows] })
          hist(who, input.lenderSlug, `${short(input.name)} ${input.version}`, `New workflow for ${input.productSlug}: ${flow.note}`)
          syncLender(input.lenderSlug, get().flows, get().endpoints)
          const body = { id: flow.id, productSlug: input.productSlug, name: input.name, version: input.version, batch: input.batch, ...(input.start.kind === 'copy' ? { from: input.start.fromId } : { routes: input.start.files }) }
          send(input.lenderSlug, () => api<WorkflowVersion>('POST', `/lenders/${enc(input.lenderSlug)}/workflows`, body), putFlow)
          return flow
        },

        newVersionFrom: (fromId, who) => {
          const from = get().flows.find((f) => f.id === fromId)
          if (!from) throw new Error('workflow not found')
          const v = nextFlowVersion(get().flows, from.name)
          const flow = copyFlow(from, v, who, nowIso())
          set({ flows: [flow, ...get().flows] })
          hist(who, from.lenderSlug, `${short(from.name)} ${v}`, `New version from ${from.version}`)
          syncLender(from.lenderSlug, get().flows, get().endpoints)
          send(from.lenderSlug, () => api<WorkflowVersion>('POST', `/workflows/${enc(fromId)}/new-version`, { id: flow.id }), putFlow)
          return flow
        },

        makeLive: (flowId, who) => {
          const f = get().flows.find((x) => x.id === flowId)
          if (!f) throw new Error('workflow not found')
          if (f.status !== 'paths_passed') throw new Error(`Make live needs "paths passed"; ${short(f.name)} ${f.version} is ${f.status.replace('_', ' ')}`)
          const ep = get().endpoints.find((e) => e.workflowName === f.name && e.env === 'uat_prod' && e.state !== 'retired')
          if (!ep) throw new Error(`No production endpoint runs ${f.name} yet: create one in 3.5 Service endpoints`)
          if (!apiMode()) return get().switchEndpoint(ep.id, f.version, who)
          const r = applySwitch(get().flows, ep, f.version, useCodeStore.getState().batches, who, nowIso())
          set({ flows: r.flows, endpoints: get().endpoints.map((e) => (e.id === ep.id ? r.endpoint : e)) })
          // the API starts the version's production container first, then switches: the statuses come back with it
          send(f.lenderSlug, () => api<ServiceEndpoint>('POST', `/workflows/${enc(flowId)}/make-live`, {}), (e) => { putEndpoint(e); void refreshFlows(f.lenderSlug).catch(() => {}) })
          return r.endpoint
        },

        switchEndpoint: (endpointId, toVersion, who) => {
          const ep = get().endpoints.find((e) => e.id === endpointId)
          if (!ep) throw new Error('endpoint not found')
          const r = applySwitch(get().flows, ep, toVersion, useCodeStore.getState().batches, who, nowIso())
          set({ flows: r.flows, endpoints: get().endpoints.map((e) => (e.id === endpointId ? r.endpoint : e)) })
          hist(who, ep.lenderSlug, `Endpoint ${ep.id}`, `Switched ${short(ep.workflowName)} ${r.from} → ${toVersion}`)
          syncLender(ep.lenderSlug, get().flows, get().endpoints)
          send(ep.lenderSlug, () => api<ServiceEndpoint>('POST', `/endpoints/${enc(endpointId)}/switch`, { toVersion }), (e) => { putEndpoint(e); void refreshFlows(ep.lenderSlug).catch(() => {}) })
          return r.endpoint
        },

        setEndpointState: (endpointId, state, reason, who) => {
          const ep = get().endpoints.find((e) => e.id === endpointId)
          if (!ep) return
          if (ep.state === 'retired') throw new Error(`${ep.id} is retired; create a new endpoint`)
          set({ endpoints: get().endpoints.map((e) => (e.id === endpointId ? { ...e, state, suspendReason: state === 'suspended' ? reason : null, stats: state === 'active' ? { ...e.stats, errorsWindow: 0 } : e.stats } : e)) })
          const verb = state === 'suspended' ? 'Suspended' : state === 'retired' ? 'Retired' : 'Resumed'
          hist(who, ep.lenderSlug, `Endpoint ${ep.id}`, `${verb}${reason ? `: “${reason}”` : ''}`)
          if (state !== 'active') local(() => useHubStore.getState().addAlert({ lenderSlug: ep.lenderSlug, severity: state === 'retired' ? 'info' : 'warning', kind: 'info', text: `Endpoint ${ep.id} ${verb.toLowerCase()} by ${who}${reason ? ` (“${reason}”)` : ''}; the web app gets “lender unavailable” for it.` }))
          syncLender(ep.lenderSlug, get().flows, get().endpoints)
          send(ep.lenderSlug, () => api<ServiceEndpoint>('POST', `/endpoints/${enc(endpointId)}/state`, { state, reason }), putEndpoint)
        },

        createEndpoint: (input, who) => {
          if (!/^[a-z0-9]+(\.[a-z0-9-]+)+$/.test(input.id)) throw new Error('endpoint_id looks like lender.product, e.g. moneyview.personal-uat')
          if (get().endpoints.some((e) => e.id === input.id)) throw new Error(`${input.id} already exists`)
          const ep: ServiceEndpoint = { ...input, state: 'active', suspendReason: null, stats: { runs24h: 0, avgMs: null, errors24h: 0, errorsWindow: 0 }, lastSwitch: null, createdAt: nowIso() }
          set({ endpoints: [...get().endpoints, ep] })
          hist(who, input.lenderSlug, `Endpoint ${ep.id}`, `Created: ${short(ep.workflowName)} ${ep.workflowVersion} · ${ep.env === 'uat' ? 'UAT only' : 'UAT + production'}`)
          syncLender(input.lenderSlug, get().flows, get().endpoints)
          const body = { id: input.id, productSlug: input.productSlug, workflowName: input.workflowName, workflowVersion: input.workflowVersion, env: input.env }
          send(input.lenderSlug, () => api<ServiceEndpoint>('POST', `/lenders/${enc(input.lenderSlug)}/endpoints`, body), putEndpoint)
          return ep
        },

        recordRun: (run) => {
          if (apiMode()) {
            // the API recorded it (the test call): show it at once, then the counters and alerts as the API has them
            set({ runs: [run, ...get().runs.filter((r) => r.id !== run.id)] })
            remote(() => refreshFlows(run.lenderSlug))
            return
          }
          const rule = useSettingsStore.getState().settings.errorRule
          let raise: ServiceEndpoint | null = null
          const endpoints = get().endpoints.map((e) => {
            if (e.id !== run.endpointId) return e
            const runs = e.stats.runs24h + 1
            const avgMs = Math.round(((e.stats.avgMs ?? run.ms) * e.stats.runs24h + run.ms) / runs)
            const err = run.answer === 'error' ? 1 : 0
            const next = { ...e, stats: { runs24h: runs, avgMs, errors24h: e.stats.errors24h + err, errorsWindow: e.stats.errorsWindow + err } }
            if (err && next.stats.errorsWindow === rule.errors) raise = next
            return next
          })
          set({ runs: [run, ...get().runs], endpoints })
          const hit = raise as ServiceEndpoint | null
          if (hit) useHubStore.getState().addAlert({ lenderSlug: hit.lenderSlug, severity: 'danger', kind: 'error_rate', text: `Endpoint ${hit.id}: ${rule.errors} errors within ${rule.minutes} min (last: ${run.steps.at(-1)?.replyCode ?? 'error'}). Check the lender's API and the run log.` })
        },

        reset: () => {
          if (apiMode()) { void loadFlowsFromHub(useHubStore.getState().lenders.map((l) => l.slug)); return }
          set({ flows: FLOWS, endpoints: ENDPOINTS, runs: RUNS })
        },
      }
    },
    // the API's copy is never kept in this browser: it is read again at every sign-in
    { name: apiMode() ? 'lender-hub-portal-flows-api' : 'lender-hub-portal-flows', version: 1, partialize: (s) => (apiMode() ? ({} as Partial<FlowState>) : s) },
  ),
)
